import { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import styles from "./Calendar.module.css";
import { getDayName, getMonthName, ICalendarEvent, ICalendarProps } from "./CalendarUtils";

/**
 * Reusable Calendar component for displaying dates with events
 * Features:
 * - Romanian localization using Intl API
 * - Customizable event rendering
 * - Month navigation
 * - Date selection with callbacks
 * - Responsive design with Material-UI
 */
export default function Calendar({
  events = [],
  onDateSelect,
  onMonthChange,
  initialDate = new Date(),
  renderEventIndicator,
  className,
  showNavigation = true,
  locale = 'ro-RO',
  // New props
  rowHeight = 'auto',
  cellHeight = 60,
  showMonthNavigation = false,
  currentDate: externalCurrentDate,
  onNavigateMonth,
  renderDayCell
}: ICalendarProps) {
  // Current displayed month/year - use external control if provided
  const [internalCurrentDate, setInternalCurrentDate] = useState(initialDate);
  const currentDate = externalCurrentDate || internalCurrentDate;
  const setCurrentDate = (date: Date) => {
    if (externalCurrentDate && onNavigateMonth) {
      // If externally controlled, don't update internal state
      const direction = date > currentDate ? 'next' : 'prev';
      onNavigateMonth(direction);
    } else {
      setInternalCurrentDate(date);
    }
  };
  
  // Currently selected day number
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  // Generate Romanian day names starting from Monday (index 1)
  // Array maps: [Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday]
  const dayNames = [1, 2, 3, 4, 5, 6, 0].map(dayIndex => getDayName(dayIndex));

  /**
   * Navigate between months and trigger callback
   * @param direction - 'prev' for previous month, 'next' for next month
   */
  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(currentDate.getMonth() - 1);
    } else {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
    setSelectedDay(null); // Reset selection when changing months
    onMonthChange?.(newDate);
  };

  /**
   * Generate array of days for the current month
   * Includes null values for empty cells at the beginning
   * @returns Array of day numbers and null values
   */
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Adjust for Monday start

    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  /**
   * Get events that occur on a specific day
   * @param day - Day number (1-31)
   * @returns Array of events for that day
   */
  const getEventsForDay = (day: number): ICalendarEvent[] => {
    if (!day) return [];
    // Format: YYYY-MM-DD
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter((event:any) => event.date.startsWith(dateStr));
  };

  /**
   * Handle clicking on a calendar day
   * @param day - Day number or null for empty cells
   */
  const handleDayClick = (day: number | null) => {
    if (!day) return;
    
    const dayEvents = getEventsForDay(day);
    setSelectedDay(day);
    
    // Create full Date object for the selected day
    const selectedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    onDateSelect?.(selectedDate, dayEvents);
  };

  /**
   * Render individual calendar day cell
   * @param day - Day number or null for empty cell
   * @param index - Array index for React key
   * @returns JSX for the calendar day
   */
  const renderCalendarDay = (day: number | null, index: number) => {
    if (!day) {
      return <div key={index} className={styles.emptyDay} />;
    }

    const dayEvents = getEventsForDay(day);
    const hasEvents = dayEvents.length > 0;
    const hasEnrolledEvents = dayEvents.some(event => event.isClientEnrolled);
    const isSelected = selectedDay === day;

    // Calculate dynamic cell styles for Material-UI sx prop
    const dynamicCellSx = {
      cursor: 'pointer',
      // Let CSS handle height when auto, otherwise use prop value
      ...(cellHeight !== 'auto' && {
        minHeight: `${typeof cellHeight === 'number' ? cellHeight : parseInt(cellHeight?.toString() || '60')}px`,
        height: `${typeof cellHeight === 'number' ? cellHeight : parseInt(cellHeight?.toString() || '60')}px`,
      })
    };

    // Use custom day cell renderer if provided
    if (renderDayCell) {
      return (
        <Box 
          key={index}
          onClick={() => handleDayClick(day)}
          sx={dynamicCellSx}
        >
          {renderDayCell(day, dayEvents, isSelected)}
        </Box>
      );
    }

    // Default day cell rendering
    return (
      <Box 
        key={index} 
        className={`${styles.calendarDay} ${hasEvents ? styles.hasEvents : ''} ${hasEnrolledEvents ? styles.hasEnrolledEvents : ''} ${isSelected ? styles.selectedDay : ''}`}
        onClick={() => handleDayClick(day)}
        sx={dynamicCellSx}
      >
        <Typography variant="body2" className={styles.dayNumber}>
          {day}
        </Typography>
        {hasEnrolledEvents && (
          <Typography 
            variant="caption" 
            className={styles.enrolledText}
            sx={{ 
              fontSize: '9px',
              color: '#4caf50',
              fontWeight: 600,
              textAlign: 'center',
              lineHeight: 1
            }}
          >
            Înscris
          </Typography>
        )}
        {hasEvents && (
          <div className={styles.eventIndicator}>
            {renderEventIndicator ? (
              renderEventIndicator(dayEvents)
            ) : (
              <Typography variant="caption" className={styles.eventCount}>
                {dayEvents.length}
              </Typography>
            )}
          </div>
        )}
      </Box>
    );
  };

  const calendarDaysGridStyle = {
    ...(rowHeight !== 'auto' && {
      minHeight: typeof rowHeight === 'number' ? `${rowHeight}px` : rowHeight
    }),
    '& > div': cellHeight === 'auto' ? {} : {
      minHeight: `${typeof cellHeight === 'number' ? cellHeight : parseInt(cellHeight?.toString() || '60')}px !important`,
      height: `${typeof cellHeight === 'number' ? cellHeight : parseInt(cellHeight?.toString() || '60')}px !important`,
    }
  };

  return (
    <Card className={`${styles.calendarCard} ${className || ''}`}>
      <CardContent>
        {/* Month Navigation - New feature */}
        {showMonthNavigation && (
          <Box className={styles.monthNavigation}>
            <IconButton 
              onClick={() => navigateMonth('prev')}
              className={styles.monthNavButton}
              aria-label="Previous month"
            >
              <ChevronLeft />
            </IconButton>
            
            <Typography variant="h4" className={styles.monthNavTitle}>
              {getMonthName(currentDate.getMonth())} {currentDate.getFullYear()}
            </Typography>
            
            <IconButton 
              onClick={() => navigateMonth('next')}
              className={styles.monthNavButton}
              aria-label="Next month"
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}

        {/* Calendar Header - Month navigation */}
        {showNavigation && (
          <Box className={styles.calendarHeader}>
            <IconButton 
              onClick={() => navigateMonth('prev')}
              className={styles.navButton}
              aria-label="Previous month"
            >
              <ChevronLeft />
            </IconButton>
            
            <Typography variant="h5" className={styles.monthYear}>
              {getMonthName(currentDate.getMonth())} {currentDate.getFullYear()}
            </Typography>
            
            <IconButton 
              onClick={() => navigateMonth('next')}
              className={styles.navButton}
              aria-label="Next month"
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}

        {/* Day names header - Mon, Tue, Wed, etc. */}
        <Box className={styles.dayNamesHeader}>
          {dayNames.map((dayName, index) => (
            <Typography key={index} variant="caption" className={styles.dayName}>
              {dayName}
            </Typography>
          ))}
        </Box>
        
        {/* Calendar grid - 7x6 grid of days with custom dimensions */}
        <Box 
          className={styles.calendarDaysGrid}
          sx={calendarDaysGridStyle}
        >
          {getDaysInMonth().map((day, index) => renderCalendarDay(day, index))}
        </Box>
      </CardContent>
    </Card>
  );
}